import { createFileRoute, Link } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { motion, useReducedMotion } from "framer-motion"
import { useEffect, useRef, useState } from "react"
import { api } from "../../../../convex/_generated/api"
import type { Id } from "../../../../convex/_generated/dataModel"
import { analyticsEvents } from "@/lib/analytics/events"
import { trackEvent } from "@/lib/analytics/posthog"
import { inNumberRange, parseIntegerDraft } from "@/lib/numberDraft"

export const Route = createFileRoute("/_authed/admin/magic-numbers")({
  component: MagicNumbersAdmin,
})

const DECADES = ["60s", "70s", "80s", "90s", "00s", "10s", "20s"] as const
const ROUNDS = [0, 1, 2, 3, 4] as const

type Kind = "player-claimed" | "host-disabled-game" | "host-disabled-global"
type GlobalKind = Extract<Kind, "host-disabled-global">
type GlobalLock = { kind: GlobalKind; isLegacyAllRound: boolean }
type ScopedMagicLock = {
  _id: Id<"magicNumberLocks">
  number: number
  roundIndex?: number
  kind: Exclude<Kind, "host-disabled-global">
  gameId?: Id<"games">
  roundId?: Id<"rounds">
  participantId?: Id<"participants">
  isLegacyAllRound: boolean
  lockedAt: number
}

export function MagicNumbersAdmin() {
  const [decade, setDecade] = useState<(typeof DECADES)[number]>("80s")
  const [roundIndex, setRoundIndex] = useState<number>(0)
  const [totalNumbersDraft, setTotalNumbersDraft] = useState("99")
  const [savingTotal, setSavingTotal] = useState(false)

  const locks = useQuery(api.magicNumbers.list, { decade, roundIndex })
  const config = useQuery(api.magicNumbers.config, { decade, roundIndex })
  const savedTotalNumbers = config?.totalNumbers ?? 99
  const usage = useQuery(api.magicNumbers.usage, {
    decade,
    roundIndex,
    totalNumbers: savedTotalNumbers,
  })
  const disableGlobal = useMutation(api.magicNumbers.disableGlobal)
  const enableGlobal = useMutation(api.magicNumbers.enableGlobal)
  const setScopedTotalNumbers = useMutation(api.magicNumbers.setTotalNumbers)
  const resetAll = useMutation(api.magicNumbers.resetAll)
  const [error, setError] = useState<string | null>(null)
  const [resetNotice, setResetNotice] = useState<string | null>(null)
  const [resettingEverything, setResettingEverything] = useState(false)
  const [pendingNumber, setPendingNumber] = useState<number | null>(null)
  const totalNumbers = parseIntegerDraft(totalNumbersDraft)
  const totalIsValid = inNumberRange(totalNumbers, { min: 1, max: 999 })
  const totalHasChanges = totalIsValid && totalNumbers !== savedTotalNumbers
  const displayedTotalNumbers = totalIsValid ? totalNumbers : savedTotalNumbers

  useEffect(() => {
    setTotalNumbersDraft(String(config?.totalNumbers ?? 99))
  }, [config?.totalNumbers, decade, roundIndex])

  // Build a map: number → admin burn state. Player/game burns are shown below
  // for context; only admin burns are toggled from the number grid.
  const lockMap = new Map<number, GlobalLock>()
  for (const l of locks ?? []) {
    if (l.kind !== "host-disabled-global") continue
    const existing = lockMap.get(l.number)
    lockMap.set(l.number, {
      kind: l.kind,
      isLegacyAllRound: existing?.isLegacyAllRound || l.isLegacyAllRound,
    })
  }
  const scopedLocks = (locks ?? []).filter(
    (lock) => lock.kind !== "host-disabled-global",
  ) as ScopedMagicLock[]

  async function handleClick(n: number) {
    setError(null)
    setResetNotice(null)
    setPendingNumber(n)
    try {
      const current = lockMap.get(n)
      if (current?.kind === "host-disabled-global") {
        await enableGlobal({ decade, number: n, roundIndex })
      } else {
        await disableGlobal({ decade, number: n, roundIndex })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setPendingNumber(null)
    }
  }

  async function saveTotalNumbers() {
    if (!totalIsValid || totalNumbers === null) return
    setError(null)
    setResetNotice(null)
    setSavingTotal(true)
    try {
      await setScopedTotalNumbers({
        decade,
        roundIndex,
        totalNumbers,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      setTotalNumbersDraft(String(savedTotalNumbers))
    } finally {
      setSavingTotal(false)
    }
  }

  async function resetEverything() {
    if (
      !confirm(
        "Re-enable every magic number across all rounds and decades? This clears player claims and every burn. This cannot be undone.",
      )
    ) {
      return false
    }

    if (resettingEverything) return true
    setError(null)
    setResetNotice(null)
    setResettingEverything(true)
    try {
      const result = await resetAll({})
      const label = result.deleted === 1 ? "lock" : "locks"
      setResetNotice(
        `Reset complete. ${result.deleted} magic-number ${label} cleared.`,
      )
      trackEvent(analyticsEvents.lcMagicNumbersResetByHost, {
        deletedCount: result.deleted,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setResettingEverything(false)
    }
    return true
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Magic numbers</h1>
        <p className="mt-1 text-sm text-[color:var(--muted-foreground)]">
          Permanent burns by round slot and decade. In-game locks burn the same
          combination across future games; admin burns let you retire a slot
          manually.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="block">
          <span className="mb-1 block text-xs font-medium text-[color:var(--muted-foreground)]">
            Round
          </span>
          <div className="inline-flex rounded-md border border-[color:var(--border)] bg-[color:var(--background)] p-0.5">
            {ROUNDS.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRoundIndex(r)}
                className={[
                  "rounded px-2.5 py-1 text-xs font-semibold transition-colors",
                  roundIndex === r
                    ? "bg-primary text-primary-foreground"
                    : "text-[color:var(--muted-foreground)] hover:text-[color:var(--foreground)]",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {roundLabel(r)}
              </button>
            ))}
          </div>
        </div>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-[color:var(--muted-foreground)]">
            Decade
          </span>
          <select
            value={decade}
            onChange={(e) =>
              setDecade(e.target.value as (typeof DECADES)[number])
            }
            className="rounded-md border border-[color:var(--border)] bg-[color:var(--background)] px-3 py-1.5 text-sm"
          >
            {DECADES.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </label>
        <div className="block">
          <span className="mb-1 block text-xs font-medium text-[color:var(--muted-foreground)]">
            Total numbers
          </span>
          <div className="flex items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={totalNumbersDraft}
              disabled={config === undefined || savingTotal}
              onChange={(e) => setTotalNumbersDraft(e.target.value)}
              aria-invalid={!totalIsValid || undefined}
              className="w-24 rounded-md border border-[color:var(--border)] bg-[color:var(--background)] px-3 py-1.5 text-sm disabled:cursor-wait disabled:opacity-60"
            />
            <button
              type="button"
              onClick={saveTotalNumbers}
              disabled={
                config === undefined ||
                savingTotal ||
                !totalIsValid ||
                !totalHasChanges
              }
              className="rounded-md border border-[color:var(--border)] bg-[color:var(--background)] px-3 py-1.5 text-xs font-semibold text-[color:var(--foreground)] transition-colors hover:bg-[color:var(--muted)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {savingTotal ? "Saving..." : "Save total"}
            </button>
          </div>
          <p className="mt-1 text-[11px] text-[color:var(--muted-foreground)]">
            Applies only to {roundLabel(roundIndex)} · {decade}. Enter 1-999.
          </p>
        </div>
      </div>

      {usage && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Free" value={usage.free} />
          <Stat label="Burned in games" value={usage.claimed} />
          <Stat label="Admin burns" value={usage.hostDisabled} />
          <Stat label="% burned" value={usage.percentClaimed} suffix="%" />
        </div>
      )}

      {error && (
        <p className="rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-900">
          {error}
        </p>
      )}
      {resetNotice && (
        <p className="rounded-md border border-emerald-300 bg-emerald-50 p-2 text-xs text-emerald-900">
          {resetNotice}
        </p>
      )}

      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-sm font-semibold">
            {roundLabel(roundIndex)} · {decade}
          </h2>
          <Legend />
        </div>
        <div className="grid grid-cols-5 gap-1.5 sm:grid-cols-10">
          {Array.from(
            { length: displayedTotalNumbers },
            (_, i) => i + 1,
          ).map((n) => {
            const kind = lockMap.get(n)
            const isPending = pendingNumber === n
            return (
              <NumberCell
                key={n}
                n={n}
                roundIndex={roundIndex}
                kind={kind}
                isPending={isPending}
                onClick={() => handleClick(n)}
              />
            )
          })}
        </div>
      </div>

      <ScopedLocksList locks={scopedLocks} />

      <ResetActions
        onResetEverything={resetEverything}
        resetting={resettingEverything}
      />
    </div>
  )
}

function Legend() {
  const items: { label: string; swatch: string }[] = [
    {
      label: "Free",
      swatch: "border-[color:var(--border)] bg-[color:var(--background)]",
    },
    {
      label: "Admin burn",
      swatch: "border-amber-400 bg-amber-100",
    },
  ]
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-[color:var(--muted-foreground)]">
      {items.map((it) => (
        <li key={it.label} className="inline-flex items-center gap-1.5">
          <span
            aria-hidden
            className={`inline-block h-3 w-3 rounded-sm border ${it.swatch}`}
          />
          {it.label}
        </li>
      ))}
    </ul>
  )
}

function NumberCell({
  n,
  roundIndex,
  kind,
  isPending,
  onClick,
}: {
  n: number
  roundIndex: number
  kind: GlobalLock | undefined
  isPending: boolean
  onClick: () => void
}) {
  const reduced = useReducedMotion()
  const label =
    kind?.kind === "host-disabled-global"
      ? kind.isLegacyAllRound
        ? `#${n} legacy all-round admin burn — click to re-enable`
        : `${roundLabel(roundIndex)} #${n} admin burn — click to re-enable`
      : `${roundLabel(roundIndex)} #${n} free — click to add admin burn`

  const baseColor =
    kind?.kind === "host-disabled-global"
      ? "border-amber-400 bg-amber-100 text-amber-900"
      : "border-[color:var(--border)] bg-[color:var(--background)] hover:border-primary/60 hover:bg-primary/5 hover:text-[color:var(--foreground)]"

  return (
    <motion.button
      type="button"
      onClick={onClick}
      disabled={isPending}
      layout="position"
      whileHover={
        !isPending && !reduced
          ? { y: -1, transition: { duration: 0.12, ease: "easeOut" } }
          : undefined
      }
      whileTap={
        !isPending && !reduced
          ? { scale: 0.92, transition: { duration: 0.08, ease: "easeOut" } }
          : undefined
      }
      transition={{ duration: 0.18, ease: "easeOut" }}
      aria-label={label}
      aria-pressed={kind?.kind === "host-disabled-global"}
      title={label}
      className={[
        "aspect-square rounded-md border text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--background)]",
        baseColor,
        isPending && "animate-pulse",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {n}
    </motion.button>
  )
}

function ScopedLocksList({
  locks,
}: {
  locks: ScopedMagicLock[]
}) {
  if (locks.length === 0) return null

  return (
    <section className="rounded-md border border-[color:var(--border)] p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">Round-scoped locks</h2>
          <p className="mt-1 text-xs text-[color:var(--muted-foreground)]">
            In-game burns for this round slot. These are global history now and
            are managed from the host round card.
          </p>
        </div>
        <span className="font-mono text-[11px] uppercase tracking-wide text-[color:var(--muted-foreground)]">
          {locks.length}
        </span>
      </div>
      <ul className="mt-3 divide-y divide-[color:var(--border)] rounded-md border border-[color:var(--border)]">
        {locks.map((lock) => (
          <li
            key={lock._id}
            className="flex flex-col gap-2 px-3 py-2 text-xs sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <p className="font-mono font-semibold">
                #{lock.number} ·{" "}
                {lock.kind === "host-disabled-game"
                  ? "round burn"
                  : "player pick"}
              </p>
              <p className="mt-0.5 truncate text-[color:var(--muted-foreground)]">
                {lock.roundIndex !== undefined
                  ? roundLabel(lock.roundIndex)
                  : `Round ${lock.roundId ?? "unknown"}`}{" "}
                ·{" "}
                {new Date(lock.lockedAt).toLocaleString()}
              </p>
            </div>
            {lock.gameId && (
              <Link
                to="/host/games/$gameId"
                params={{ gameId: lock.gameId }}
                search={{ view: "run" }}
                className="shrink-0 rounded-full border border-[color:var(--border)] px-2.5 py-1 font-semibold text-[color:var(--muted-foreground)] transition-colors hover:bg-[color:var(--muted)] hover:text-[color:var(--foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                Open round
              </Link>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

function ResetActions({
  onResetEverything,
  resetting,
}: {
  onResetEverything: () => Promise<boolean>
  resetting: boolean
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[color:var(--border)] pt-4">
      <div>
        <p className="text-sm font-semibold">Reset all magic numbers</p>
        <p className="mt-1 text-xs text-[color:var(--muted-foreground)]">
          Re-enables every number in every round and decade. Number totals stay
          unchanged.
        </p>
      </div>
      <DangerButton
        onClick={onResetEverything}
        disabled={resetting}
        className="border-red-300 text-red-700 hover:bg-red-50"
      >
        {resetting ? "Resetting everything..." : "Reset everything"}
      </DangerButton>
    </div>
  )
}

function roundLabel(roundIndex: number) {
  return `R${roundIndex + 1}`
}

function DangerButton({
  onClick,
  disabled = false,
  className,
  children,
}: {
  onClick: () => Promise<boolean>
  disabled?: boolean
  className?: string
  children: React.ReactNode
}) {
  const reduced = useReducedMotion()
  const [shake, setShake] = useState(0)

  async function handle() {
    const confirmed = await onClick()
    if (!confirmed && !reduced) {
      // Cancelled at confirm() — give a small "nope" shake to acknowledge the
      // dismissal without making the button feel broken.
      setShake((s) => s + 1)
    }
  }

  return (
    <motion.button
      type="button"
      onClick={handle}
      disabled={disabled}
      key={shake}
      animate={
        shake && !reduced
          ? { x: [0, -3, 3, -2, 2, 0] }
          : { x: 0 }
      }
      transition={{ duration: 0.32, ease: "easeOut" }}
      whileTap={!disabled && !reduced ? { scale: 0.97 } : undefined}
      className={[
        "rounded-md border px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--background)] disabled:cursor-wait disabled:opacity-60",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </motion.button>
  )
}

function Stat({
  label,
  value,
  suffix,
}: {
  label: string
  value: number
  suffix?: string
}) {
  return (
    <div className="rounded-md border border-[color:var(--border)] p-3">
      <p className="text-[10px] uppercase tracking-wide text-[color:var(--muted-foreground)]">
        {label}
      </p>
      <p className="mt-1 font-mono text-2xl font-semibold tabular-nums">
        <AnimatedCount value={value} />
        {suffix ?? ""}
      </p>
    </div>
  )
}

function AnimatedCount({ value }: { value: number }) {
  const reduced = useReducedMotion()
  const [display, setDisplay] = useState(value)
  const fromRef = useRef(value)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    if (reduced) {
      setDisplay(value)
      fromRef.current = value
      return
    }
    const from = fromRef.current
    const to = value
    if (from === to) return
    const start = performance.now()
    const duration = 380
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration)
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3)
      const next = Math.round(from + (to - from) * eased)
      setDisplay(next)
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        fromRef.current = to
        rafRef.current = null
      }
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
      fromRef.current = display
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, reduced])

  return <>{display}</>
}
